import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { HardHat, Loader2, ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
  const { requestPasswordReset } = useAuth();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [devToken, setDevToken] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await requestPasswordReset(email);
      setSent(true);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-8">
      <Card className="w-full max-w-md p-6 sm:p-8 border border-border shadow-sm">
        <div className="flex items-center gap-2 mb-6">
          <div className="h-9 w-9 rounded-md bg-navy flex items-center justify-center">
            <HardHat className="h-5 w-5 text-white" strokeWidth={2.5} />
          </div>
          <div className="font-display font-bold text-xl text-slate-900">BuildTrack</div>
        </div>

        {!sent ? (
          <>
            <div className="mb-6">
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Password reset</div>
              <h1 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Forgot your password?</h1>
              <p className="text-sm text-slate-500 mt-1">Enter your email and we&apos;ll send you a reset link.</p>
            </div>
            <form onSubmit={onSubmit} className="space-y-5" data-testid="forgot-form">
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wider text-slate-600">Email</Label>
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="h-12" data-testid="forgot-email" placeholder="you@builder.com.au" />
              </div>
              {error && (
                <div className="text-sm text-status-danger-text bg-status-danger-bg border border-status-danger-border rounded-md px-3 py-2" data-testid="forgot-error">
                  {error}
                </div>
              )}
              <Button type="submit" disabled={loading} data-testid="forgot-submit" className="w-full h-12 bg-brandblue hover:bg-brandblue-hover text-white text-base font-semibold">
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {loading ? "Sending…" : "Send reset link"}
              </Button>
            </form>
          </>
        ) : (
          <div className="text-center space-y-4" data-testid="forgot-sent">
            <div className="mx-auto h-14 w-14 rounded-full bg-status-paid-bg text-status-paid-text flex items-center justify-center font-display text-2xl font-bold">✓</div>
            <h2 className="font-display text-2xl font-bold text-slate-900">Check your email</h2>
            <p className="text-sm text-slate-500">
              If an account exists for that email, we&apos;ve sent a password reset link. The link is valid for 1 hour.
            </p>
            {devToken && (
              <div className="text-left bg-slate-50 border border-border rounded-md p-3 space-y-2">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Dev mode — reset link</div>
                <Link to={`/reset-password?token=${devToken}`} className="text-brandblue text-xs font-semibold hover:underline break-all" data-testid="forgot-dev-link">
                  /reset-password?token={devToken}
                </Link>
              </div>
            )}
          </div>
        )}

        <div className="text-sm text-slate-500 mt-6 text-center">
          <Link to="/login" className="text-brandblue font-semibold hover:underline inline-flex items-center gap-1" data-testid="link-back-login">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
          </Link>
        </div>
      </Card>
    </div>
  );
}
